<template>
  <van-row justify="center" class="page-wrapper">
    <loader :show="showLoader" />
    <payment-flow-animation
      v-if="showPaymentAnimation"
      :result="paymentAnimationResult"
      flow="card"
      :success-action-label="animationActionLabels.success"
      :error-action-label="animationActionLabels.error"
      @action="handleAnimationAction"
    />

    <van-col
      v-if="paymentUrl"
      span="24"
      class="itserve-center flex-display-fix add-card-iframe-wrap"
    >
      <div class="iframe-container">
        <iframe
          :src="paymentUrl"
          allowpaymentrequest
          frameborder="0"
          class="add-card-iframe"
          ref="addCardFrame"
        ></iframe>
      </div>
    </van-col>

    <van-col v-else span="24" class="itserve-center flex-display-fix">
      <div class="add-card-screen">
        <div class="payment-card">
          <div class="form-group">
            <label for="cardNumber">{{ $t("pay.cardNumber") }}</label>
            <input
              id="cardNumber"
              v-model="cardData.number"
              type="text"
              inputmode="numeric"
              maxlength="19"
              placeholder="XXXX XXXX XXXX XXXX"
              class="payment-input"
              @input="formatCardNumber"
            />
          </div>

          <div class="card-meta-row">
            <div class="form-group form-group-half">
              <label for="cardExpiry">{{ $t("pay.cardExpiry") }}</label>
              <input
                id="cardExpiry"
                v-model="cardData.expiry"
                type="text"
                inputmode="numeric"
                maxlength="5"
                placeholder="XX/XX"
                class="payment-input"
                @input="formatExpiry"
              />
            </div>

            <div class="form-group form-group-half">
              <label for="cardCvv">CVV</label>
              <input
                id="cardCvv"
                v-model="cardData.cvv"
                type="password"
                inputmode="numeric"
                maxlength="3"
                placeholder="XXX"
                class="payment-input"
                @input="formatCvv"
              />
            </div>
          </div>
        </div>

        <div v-if="validationError" class="error-message">
          {{ validationError }}
        </div>

        <div class="pay-button-wrap">
          <button
            type="button"
            class="submit-button"
            :disabled="!canSubmit"
            @click="submitCard"
          >
            {{ $t("buttons.addCard") }}
          </button>
        </div>
      </div>
    </van-col>

  </van-row>
</template>

<script>
import Loader from "@/components/content/Loader";
import PaymentFlowAnimation from "@/components/payment/PaymentFlowAnimation";

export default {
  name: "AddCardPage",
  components: {
    Loader,
    PaymentFlowAnimation,
  },
  data() {
    return {
      watchInterval: null,
      validationError: null,
      showPaymentAnimation: false,
      paymentAnimationResult: null,
      fallbackPhone: "",
      cardData: {
        number: "",
        expiry: "",
        cvv: "",
      },
    };
  },
  computed: {
    showLoader() {
      return this.$store.state.Api.loading;
    },
    paymentUrl() {
      return this.$store.state.Payment.paymentUrl;
    },
    paymentResponse() {
      return this.$store.state.Payment.paymentResponse;
    },
    apiErrors() {
      return this.$store.state.Api.errors;
    },
    getPhone() {
      return (
        this.$store.state.Auth.phoneNumber ||
        (this.$store.state.Settings.phone &&
          this.$store.state.Settings.phone.phone_for_save_card)
      );
    },
    paymentPhone() {
      return this.getPhone || this.fallbackPhone;
    },
    canSubmit() {
      return (
        this.cardData.number.replace(/\s/g, "").length === 16 &&
        this.cardData.expiry.length === 5 &&
        this.cardData.cvv.length === 3
      );
    },
    animationActionLabels() {
      const locale = this.$i18n && this.$i18n.locale === "en"
        ? "en"
        : this.$i18n && this.$i18n.locale === "ru"
          ? "ru"
          : "uk";

      return {
        uk: {
          success: "До карток",
          error: "Спробувати ще раз",
        },
        ru: {
          success: "К картам",
          error: "Попробовать снова",
        },
        en: {
          success: "To cards",
          error: "Try again",
        },
      }[locale];
    },
  },
  async created() {
    this.$store.commit("Payment/SET_PAYMENT_URL", null);
    this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
    this.$store.commit("Payment/SET_CARD_SAVE_RESULT_MESSAGE", null);
    this.fallbackPhone = this.generateFallbackPhone();
    await this.$store.dispatch("Settings/getPhone");
    await this.$store.dispatch("Auth/setUserPhone");
    this.runCheck();
  },
  destroyed() {
    clearInterval(this.watchInterval);
  },
  methods: {
    wait(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    async refreshSavedCardsUntilVisible(savedCardId = null) {
      const normalizedId = Number(savedCardId || 0);

      for (let attempt = 0; attempt < 4; attempt += 1) {
        await this.$store.dispatch("Payment/getCardsList");

        if (!normalizedId) {
          return;
        }

        const hasCard = (this.$store.state.Payment.cards || []).some(
          (card) => Number(card.card_id || card.card_token) === normalizedId
        );

        if (hasCard) {
          return;
        }

        await this.wait(450);
      }
    },
    openPaymentAnimation() {
      this.showPaymentAnimation = true;
      this.paymentAnimationResult = null;
    },
    finishPaymentAnimation(result) {
      this.showPaymentAnimation = true;
      this.paymentAnimationResult = result;
    },
    async handleAnimationAction() {
      if (this.paymentAnimationResult && this.paymentAnimationResult.success) {
        this.showPaymentAnimation = false;
        this.paymentAnimationResult = null;
        await this.$router.push({ name: "PaymentCards" });
        return;
      }
      this.showPaymentAnimation = false;
      this.paymentAnimationResult = null;
    },
    formatCardNumber() {
      const digits = this.cardData.number.replace(/\D/g, "").slice(0, 16);
      const groups = digits.match(/.{1,4}/g);
      this.cardData.number = groups ? groups.join(" ") : "";
      this.validationError = null;
    },
    formatExpiry() {
      const digits = this.cardData.expiry.replace(/\D/g, "").slice(0, 4);
      if (digits.length <= 2) {
        this.cardData.expiry = digits;
      } else {
        this.cardData.expiry = `${digits.slice(0, 2)}/${digits.slice(2)}`;
      }
      this.validationError = null;
    },
    formatCvv() {
      this.cardData.cvv = this.cardData.cvv.replace(/\D/g, "").slice(0, 3);
      this.validationError = null;
    },
    generateFallbackPhone() {
      const prefixes = ["+38093", "+38096", "+38097", "+38050"];
      const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
      const randomPart = Math.floor(Math.random() * 10000000)
        .toString()
        .padStart(7, "0");
      return `${prefix}${randomPart}`;
    },
    validateForm() {
      if (!this.canSubmit) {
        this.validationError = this.$t("pay.fillCardData");
        return false;
      }
      this.validationError = null;
      return true;
    },
    buildPayload() {
      return {
        card_number: this.cardData.number.replace(/\s/g, ""),
        card_expiry: this.cardData.expiry,
        card_cvv: this.cardData.cvv,
        phone: this.paymentPhone,
      };
    },
    async submitCard() {
      if (!this.validateForm()) {
        return;
      }

      try {
        const existingCardIds = (this.$store.state.Payment.cards || [])
          .map((card) => Number(card.card_id || card.card_token))
          .filter(Boolean);

        const result = await this.$store.dispatch(
          "Payment/saveCard",
          this.buildPayload()
        );

        if (result === false && this.apiErrors && this.apiErrors.message) {
          this.finishPaymentAnimation({
            success: false,
            message: this.apiErrors.message,
          });
          return;
        }

        if (
          this.paymentResponse &&
          this.paymentResponse.success === false &&
          this.paymentResponse.message
        ) {
          this.finishPaymentAnimation({
            success: false,
            message: this.paymentResponse.message,
          });
          return;
        }

        if (
          this.paymentResponse &&
          this.paymentResponse.success === true &&
          this.paymentResponse.saved_card_id
        ) {
          const savedCardId = Number(this.paymentResponse.saved_card_id);
          const alreadyAdded = existingCardIds.indexOf(savedCardId) >= 0;

          await this.refreshSavedCardsUntilVisible(savedCardId);
          const successMessage = alreadyAdded
            ? this.$t("pay.cardAlreadyAdded")
            : this.paymentResponse.message || this.$t("pay.cardAdded");
          this.$store.commit(
            "Payment/SET_CARD_SAVE_RESULT_MESSAGE",
            successMessage
          );
          this.finishPaymentAnimation({
            success: true,
            alreadyAdded,
            message: successMessage,
          });
          return;
        }

        if (
          this.paymentResponse &&
          this.paymentResponse.success === true &&
          (this.paymentResponse.redirect_url || this.paymentResponse.url)
        ) {
          this.showPaymentAnimation = false;
          this.paymentAnimationResult = null;
          this.$store.commit(
            "Payment/SET_PAYMENT_URL",
            this.paymentResponse.redirect_url || this.paymentResponse.url
          );
          return;
        }

        if (
          this.paymentResponse &&
          this.paymentResponse.success === true &&
          (this.paymentResponse.status === 1 ||
            this.paymentResponse.payment_status === "accepted")
        ) {
          await this.refreshSavedCardsUntilVisible(
            this.paymentResponse.saved_card_id
          );
          const successMessage =
            this.paymentResponse.message || this.$t("pay.cardAdded");
          this.$store.commit(
            "Payment/SET_CARD_SAVE_RESULT_MESSAGE",
            successMessage
          );
          this.finishPaymentAnimation({
            success: true,
            message: successMessage,
          });
          return;
        }

        if (
          this.paymentResponse &&
          Object.keys(this.paymentResponse).indexOf("mode") >= 0
        ) {
          if (this.paymentResponse.mode === 1) {
            await this.refreshSavedCardsUntilVisible(
              this.paymentResponse.saved_card_id
            );
            const successMessage =
              this.paymentResponse.message || this.$t("pay.cardAdded");
            this.$store.commit(
              "Payment/SET_CARD_SAVE_RESULT_MESSAGE",
              successMessage
            );
            this.finishPaymentAnimation({
              success: true,
              message: successMessage,
            });
          } else if (!this.paymentUrl) {
            const errorMessage =
              this.paymentResponse.message || this.$t("pay.cardNotAdded");
            this.$store.commit(
              "Payment/SET_CARD_SAVE_RESULT_MESSAGE",
              errorMessage
            );
            this.finishPaymentAnimation({
              success: false,
              message: errorMessage,
            });
          }
          return;
        }

        const fallbackMessage =
          (this.paymentResponse && this.paymentResponse.message) ||
          this.$t("pay.cardNotAdded");
        this.$store.commit(
          "Payment/SET_CARD_SAVE_RESULT_MESSAGE",
          fallbackMessage
        );
        this.finishPaymentAnimation({
          success: false,
          message: fallbackMessage,
        });
      } catch (e) {
        const errorMessage = this.$t("pay.cardNotAdded");
        this.$store.commit(
          "Payment/SET_CARD_SAVE_RESULT_MESSAGE",
          errorMessage
        );
        this.finishPaymentAnimation({
          success: false,
          message: errorMessage,
        });
      }
    },
    runCheck() {
      this.watchInterval = setInterval(async () => {
        if (this.$refs.addCardFrame !== undefined) {
          try {
            const url = this.$refs.addCardFrame.contentWindow.location.href;
            if (url.match(process.env.VUE_APP_PRODUCTION_URL) !== null) {
              clearInterval(this.watchInterval);
              const path = url.replace(process.env.VUE_APP_PRODUCTION_URL, "");
              if (path === "/card/success") {
                await this.$store.dispatch("Payment/getCardsList");
              }
              this.$router.push(path);
            }
          } catch (e) {
            console.log("Waiting add card callback");
          }
        }
      }, 200);
    },
  },
};
</script>

<style scoped>
.add-card-iframe-wrap {
  padding-top: 10px;
}

.iframe-container {
  overflow: auto;
  -webkit-overflow-scrolling: touch !important;
  height: calc(100vh - 120px);
  width: 100%;
}

.iframe-container iframe {
  height: calc(100vh - 120px);
  width: 1px;
  min-width: 100%;
  *width: 100%;
}

.add-card-iframe {
  border: none !important;
}

.add-card-screen {
  width: 100%;
  max-width: 540px;
  margin: 0 auto;
  padding: 14px 12px 28px;
  box-sizing: border-box;
}

.payment-card,
.phone-card {
  width: 100%;
  margin-bottom: 14px;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 0;
  box-sizing: border-box;
}

.form-group {
  width: 100%;
  margin-bottom: 14px;
  box-sizing: border-box;
}

.form-group label {
  display: block;
  margin-bottom: 10px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 15px;
  font-weight: 700;
  text-align: left;
}

.payment-input {
  display: block;
  width: 100%;
  height: 56px;
  padding: 0 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.015);
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  outline: none;
  box-sizing: border-box;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.02);
}

.payment-input::placeholder {
  color: rgba(255, 255, 255, 0.38);
}

.payment-input:focus {
  border-color: rgba(90, 124, 255, 0.78);
  box-shadow:
    0 0 0 3px rgba(79, 120, 246, 0.16),
    inset 0 0 0 1px rgba(255, 255, 255, 0.02);
}

.card-meta-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  width: 100%;
}

.form-group-half,
.no-margin {
  margin-bottom: 0;
}

.secondary-button,
.submit-button {
  display: block;
  width: 100%;
  border: none;
  cursor: pointer;
  box-sizing: border-box;
  transition: background-color 0.2s ease, opacity 0.2s ease, transform 0.2s ease;
}

.secondary-button {
  height: 48px;
  margin-top: 14px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
}

.secondary-button:active {
  transform: translateY(1px);
}

.error-message {
  margin: 0 0 16px;
  padding: 12px 14px;
  color: #f87c7c;
  background: rgba(248, 124, 124, 0.16);
  border-left: 4px solid #f87c7c;
  border-radius: 8px;
  text-align: center;
  font-weight: 700;
}

.pay-button-wrap {
  width: 100%;
  margin-top: 6px;
}

.submit-button {
  display: block;
  width: 100% !important;
  max-width: none !important;
  height: 56px;
  border-radius: 14px;
  background: linear-gradient(135deg, #4272ff 0%, #5f8cff 100%);
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  box-shadow: 0 16px 32px rgba(79, 120, 246, 0.24);
}

.submit-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #4b7aff 0%, #6a95ff 100%);
}

.submit-button:active:not(:disabled) {
  transform: translateY(1px);
}

.submit-button:disabled {
  background: rgba(79, 120, 246, 0.38);
  color: rgba(255, 255, 255, 0.62);
  cursor: not-allowed;
  box-shadow: none;
}

@media (min-width: 768px) {
  .add-card-screen {
    max-width: 560px;
    padding-left: 18px;
    padding-right: 18px;
  }
}

@media (max-width: 520px) {
  .add-card-screen {
    padding-left: 10px;
    padding-right: 10px;
  }

  .form-group label {
    font-size: 14px;
  }
}

@media (max-width: 375px) {
  .card-meta-row {
    grid-template-columns: 1fr;
  }

  .payment-input,
  .submit-button {
    height: 52px;
  }

  .payment-input {
    font-size: 15px;
  }

  .submit-button {
    font-size: 16px;
  }
}
</style>
